// === SNAPSHOT D'ÉCRAN DE PRÉPARATION ===
// v0.38.17 — Garde-fou anti-perte d'items lors d'un refresh navigateur pendant
// la RunPrepScene. Chaque équipement/déséquipement déplace les items entre
// le coffre (persisté localStorage via CampManager) et l'InventoryManager
// (mémoire seule). Sans ce snapshot, un refresh entre la prépa et le départ
// du run faisait disparaître tous les items pris dans le coffre.
//
// Mécanisme :
// - À chaque modification dans RunPrepScene → savePrepSnapshot(inv).
// - Au boot (BootScene), si un snapshot existe, on remet tous les items
//   dans le coffre avant que le joueur ne voie l'écran principal.
// - Quand un run démarre vraiment (buildTeamAndStart) → clearPrepSnapshot().
// - Quand le joueur clique "← Camp" (déjà tout déséquipé) → clearPrepSnapshot().
//
// La persistance est volontairement séparée de RunManager.SAVE_KEY car la
// prépa n'est PAS un run en cours (pas de map, pas d'équipe, etc.) — c'est
// juste un état transitoire.

import { InventoryManager } from './InventoryManager';
import { CampManager } from './CampManager';
import { PERSONAL_SLOTS } from '../types/Item.types';

const STORAGE_KEY = 'deadtide_prep_snapshot';

/** Format sérialisé du snapshot — réutilise la sérialisation d'InventoryManager. */
interface PrepSnapshotData {
  /** Version pour éviter de désérialiser un format obsolète. */
  v: 1;
  /** Sortie de InventoryManager.serialize(). */
  inventory: ReturnType<InventoryManager['serialize']>;
}

/** Résultat de la restauration : combien d'items renvoyés au coffre. */
export interface RestoreResult {
  itemsReturned: number;
  consumablesReturned: number;
  itemsLost: number;        // coffre plein → items qu'on n'a pas pu remettre
  consumablesLost: number;
}

/** Persiste l'état courant de l'inventaire de prep en localStorage. */
export function savePrepSnapshot(inv: InventoryManager): void {
  try {
    const data: PrepSnapshotData = { v: 1, inventory: inv.serialize() as PrepSnapshotData['inventory'] };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage plein/indisponible — on ne bloque pas la prépa
  }
}

/** Vrai si un snapshot existe (sans le valider). */
export function hasPrepSnapshot(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/** Charge le snapshot brut, ou null. */
function loadRaw(): PrepSnapshotData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.v !== 1 || !data.inventory) return null;
    return data as PrepSnapshotData;
  } catch {
    return null;
  }
}

/** Efface le snapshot. À appeler quand un run démarre ou que le joueur revient au camp. */
export function clearPrepSnapshot(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Restaure les items du snapshot dans le coffre du camp.
 *
 * - Items équipés (par membre) → coffre (uid préservé pour stabilité).
 * - Sac à dos partagé → coffre.
 * - Items en transport → coffre.
 * - Consommables → coffre (nouveaux uids générés par CampManager).
 *
 * Si le coffre est plein, les items en surplus sont comptés comme "perdus"
 * pour info mais le snapshot est consommé. Le coffre du camp garde l'invariant
 * "ne dépasse jamais sa capacité".
 *
 * Le snapshot est effacé après restauration (succès ou échec partiel).
 */
export function restoreFromPrepSnapshot(camp: CampManager): RestoreResult {
  const result: RestoreResult = {
    itemsReturned: 0, consumablesReturned: 0, itemsLost: 0, consumablesLost: 0,
  };
  const data = loadRaw();
  if (!data) return result;

  const inv = data.inventory as {
    equipments?: Record<string, Record<string, { uid: string; definitionId: string } | null>>;
    backpack?: { uid: string; definitionId: string } | null;
    transport?: Array<{ uid: string; definitionId: string }>;
    consumables?: Array<{ uid: string; definitionId: string }>;
  };

  const tryStore = (item: { uid: string; definitionId: string }) => {
    if (camp.storeItem({ uid: item.uid, definitionId: item.definitionId })) {
      result.itemsReturned++;
    } else {
      result.itemsLost++;
    }
  };

  // 1) Équipements perso
  if (inv.equipments) {
    for (const slots of Object.values(inv.equipments)) {
      for (const slot of PERSONAL_SLOTS) {
        const it = slots[slot];
        if (it) tryStore(it);
      }
    }
  }
  // 2) Sac partagé
  if (inv.backpack) tryStore(inv.backpack);
  // 3) Transport
  if (Array.isArray(inv.transport)) {
    for (const it of inv.transport) tryStore(it);
  }
  // 4) Consommables — addConsumable n'a pas de capacité limite côté camp,
  //    il va toujours réussir tant que localStorage marche.
  if (Array.isArray(inv.consumables)) {
    for (const c of inv.consumables) {
      try {
        camp.addConsumable(c.definitionId);
        result.consumablesReturned++;
      } catch {
        result.consumablesLost++;
      }
    }
  }

  // Le snapshot est consommé : qu'il y ait eu des pertes ou non, on ne veut pas
  // re-restaurer au prochain boot (ce qui dupliquerait les items déjà rendus).
  clearPrepSnapshot();

  return result;
}
